import { useState } from 'react';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import { ArrowLeftIcon, PencilSimpleIcon } from '@phosphor-icons/react';
import {
  useEmailTemplates,
  useEmailTemplateVersions,
  useEmailTemplateBuiltins,
  useCreateEmailTemplateDraft,
  useCreateEmailTemplateFromBuiltin,
} from '@/api/rpc/hooks/communications/useEmailTemplates';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { AlertContainer, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/context/ToastContext';
import { EmailTemplateForm } from './EmailTemplateFormDialog';
import { EmailTemplateVersionHistoryPanel } from './EmailTemplateVersionHistoryPanel';
import { EmailTemplateVersionPickerDropdown } from './EmailTemplateVersionPickerDropdown';
import type { EmailTemplateResponse } from '@erp/rpc';

const emailTemplateLayoutRoute = getRouteApi(
  '/authenticated/settings/organization/email-templates/$templateId',
);

interface EmailTemplateEditorPageProps {
  viewingVersionNumber?: number | null;
}

export function EmailTemplateEditorPage({
  viewingVersionNumber,
}: EmailTemplateEditorPageProps = {}) {
  const navigate = useNavigate();
  const gt = useGT();
  const { showToast } = useToast();
  const { templateId } = emailTemplateLayoutRoute.useParams();
  const isCreateMode = templateId === 'new';
  // Built-ins open in the same editor shell, read-only, addressed as
  // `builtin:<key>` — they have no lineage row, versions, or Edit flow.
  const builtinKey = templateId.startsWith('builtin:')
    ? templateId.slice('builtin:'.length)
    : null;
  const { data: builtinsData, isPending: builtinsPending } =
    useEmailTemplateBuiltins();
  const builtin = builtinKey
    ? builtinsData?.builtins.find((b) => b.key === builtinKey)
    : undefined;
  const { data, isPending, error } = useEmailTemplates();
  const template = data?.templates.find(
    (candidate) => candidate.id === templateId,
  );

  const { data: versionsData, isPending: versionsPending } =
    useEmailTemplateVersions(isCreateMode || builtinKey ? '' : templateId);
  const versions = versionsData?.versions ?? [];

  const viewingVersion =
    viewingVersionNumber != null
      ? versions.find((v) => v.version === viewingVersionNumber)
      : null;

  const versionNotFound =
    viewingVersionNumber != null && versions.length > 0 && !viewingVersion;

  const currentVersion = viewingVersion
    ? viewingVersion
    : (versions.find((v) => v.id === template?.pinnedVersionId) ?? versions[0]);

  const isViewingDraft = currentVersion?.status === 'draft';
  const isViewingOldVersion =
    viewingVersion != null && viewingVersion.id !== template?.pinnedVersionId;

  const templateForForm: EmailTemplateResponse | undefined =
    viewingVersion && template
      ? {
          ...template,
          subject: viewingVersion.subject,
          htmlContent: viewingVersion.htmlContent,
          textContent: viewingVersion.textContent,
          variables: viewingVersion.variables ?? [],
          templateType: viewingVersion.templateType,
          jsonSpec: viewingVersion.jsonSpec,
        }
      : template;

  const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);

  const isEditing = isCreateMode || isViewingDraft;
  const readOnly = !isEditing;

  const createDraft = useCreateEmailTemplateDraft();
  const createFromBuiltin = useCreateEmailTemplateFromBuiltin();

  function handleCloneBuiltin(key: string) {
    createFromBuiltin.mutate(
      { builtinKey: key },
      {
        onSuccess: (result) =>
          navigate({
            to: '/settings/organization/email-templates/$templateId',
            params: { templateId: result.template.id },
          }),
        onError: (err) =>
          showToast({
            title: gt('Failed to create editable copy'),
            message: err instanceof Error ? err.message : String(err),
            color: 'error',
          }),
      },
    );
  }

  async function handleEdit() {
    if (!template) return;

    const existingDraft = versions.find((v) => v.status === 'draft');
    if (existingDraft) {
      navigate({
        to: '/settings/organization/email-templates/$templateId/v/$versionNumber',
        params: { templateId, versionNumber: String(existingDraft.version) },
      });
      return;
    }

    try {
      const { version } = await createDraft.mutateAsync({
        emailTemplateId: template.id,
      });
      navigate({
        to: '/settings/organization/email-templates/$templateId/v/$versionNumber',
        params: { templateId, versionNumber: String(version.version) },
      });
    } catch (err) {
      showToast({
        title: gt('Failed to create draft'),
        message: err instanceof Error ? err.message : String(err),
        color: 'error',
      });
    }
  }

  function returnToList() {
    navigate({ to: '/settings/organization/email-templates' });
  }

  function handleSaved() {
    navigate({
      to: '/settings/organization/email-templates/$templateId',
      params: { templateId },
    });
  }

  function handleCancelEdit() {
    navigate({
      to: '/settings/organization/email-templates/$templateId',
      params: { templateId },
    });
  }

  if (builtinKey) {
    if (builtinsPending) {
      return (
        <div className="flex items-center justify-center py-12">
          <Spinner className="h-6 w-6" />
          <span className="ml-2 text-muted-foreground">
            <T context="email template editor loading state">
              Loading email template...
            </T>
          </span>
        </div>
      );
    }
    if (!builtin) {
      return (
        <div className="space-y-4 p-3">
          <Button variant="ghost" size="sm" onClick={returnToList}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="email template editor back button">Back to templates</T>
          </Button>
          <p className="text-sm text-muted-foreground">
            <T context="email template editor not found message">
              Email template <Var>{templateId}</Var> was not found.
            </T>
          </p>
        </div>
      );
    }
    const builtinTemplate: EmailTemplateResponse = {
      id: templateId,
      organizationId: '',
      name: builtin.name,
      subject: builtin.subject,
      htmlContent: builtin.htmlContent ?? '',
      textContent: null,
      variables: builtin.variables,
      templateType: builtin.templateType,
      jsonSpec: builtin.jsonSpec,
      isDefault: false,
      isActive: true,
      pinnedVersionId: null,
      createdAt: new Date(0),
      updatedAt: new Date(0),
    };
    return (
      <div className="flex h-full min-h-0 flex-col gap-4 p-3">
        <div className="flex shrink-0 items-start justify-between gap-3">
          <Button variant="ghost" size="sm" onClick={returnToList}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="email template editor back button">Back to templates</T>
          </Button>
          {/* Built-ins have no versions or Edit flow — customizing one goes
              through an editable org copy. */}
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className="bg-muted py-1 font-normal text-muted-foreground"
            >
              <T context="read-only built-in email template pill">
                Built-in · view-only
              </T>
            </Badge>
            <Button
              size="sm"
              disabled={createFromBuiltin.isPending}
              onClick={() => handleCloneBuiltin(builtin.key)}
            >
              {createFromBuiltin.isPending ? (
                <T context="clone built-in template button while in progress">
                  Creating copy...
                </T>
              ) : (
                <T context="clone built-in template button">
                  Create Editable Copy
                </T>
              )}
            </Button>
          </div>
        </div>
        <EmailTemplateForm
          key={templateId}
          template={builtinTemplate}
          draftVersionId={null}
          onCancel={returnToList}
          onSaved={returnToList}
          expandedJsonEditor
          readOnly
        />
      </div>
    );
  }

  // Wait for the versions query too — until it resolves, the form would
  // mount with the pinned template content and remount once the viewed
  // version (or draft) arrives, flashing the wrong content.
  if ((isPending || versionsPending) && !isCreateMode) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">
          <T context="email template editor loading state">
            Loading email template...
          </T>
        </span>
      </div>
    );
  }

  if (error && !isCreateMode) {
    return (
      <div className="rounded-lg border bg-card p-4 text-sm text-destructive">
        <T context="email template editor load error">
          Error loading email template. Please return to the templates list and
          try again.
        </T>
      </div>
    );
  }

  if (!isCreateMode && !template) {
    return (
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <Button variant="ghost" size="sm" onClick={returnToList}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T context="email template editor back button">Back to templates</T>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="email template editor not found message">
            Email template <Var>{templateId}</Var> was not found.
          </T>
        </p>
      </div>
    );
  }

  if (versionNotFound) {
    return (
      <div className="space-y-4 rounded-lg border bg-card p-4">
        <Button variant="ghost" size="sm" onClick={returnToList}>
          <ArrowLeftIcon className="mr-2 h-4 w-4" />
          <T context="email template editor back button">Back to templates</T>
        </Button>
        <p className="text-sm text-muted-foreground">
          <T context="email template version not found message">
            Version <Var>{viewingVersionNumber}</Var> was not found for this
            template.
          </T>
        </p>
      </div>
    );
  }

  return (
    // p-3 matches the other settings subpages' content padding; the form
    // below renders unboxed (no card border/padding), so this is the only
    // spacing layer. h-full + flex-col let the Template Blocks split grow to
    // the bottom of the settings content pane.
    <div className="flex h-full min-h-0 flex-col gap-4 p-3">
      <div className="flex shrink-0 items-start justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={returnToList}>
            <ArrowLeftIcon className="mr-2 h-4 w-4" />
            <T context="email template editor back button">Back to templates</T>
          </Button>
        </div>

        <div className="flex items-center gap-2">
          {!isCreateMode && template && currentVersion && (
            <EmailTemplateVersionPickerDropdown
              templateId={template.id}
              pinnedVersionId={template.pinnedVersionId}
              currentVersionNumber={currentVersion.version}
              currentVersionId={currentVersion.id}
              currentStatus={currentVersion.status}
              currentIsPinned={currentVersion.id === template.pinnedVersionId}
              onOpenFullHistory={() => setIsVersionHistoryOpen(true)}
            />
          )}

          {readOnly && !isViewingOldVersion && template && (
            <Button
              size="sm"
              onClick={handleEdit}
              disabled={createDraft.isPending}
            >
              <PencilSimpleIcon className="mr-1.5 h-4 w-4" />
              {createDraft.isPending ? (
                <T>Creating draft...</T>
              ) : versions.some((v) => v.status === 'draft') ? (
                <T>Resume draft</T>
              ) : (
                <T>Edit</T>
              )}
            </Button>
          )}
        </div>
      </div>

      {isViewingOldVersion && viewingVersion && !isViewingDraft && (
        <AlertContainer>
          <AlertDescription>
            <T>
              You are viewing v<Var>{viewingVersion.version}</Var>, which is not
              the published version.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}

      {isViewingDraft && (
        <AlertContainer>
          <AlertDescription>
            <T>
              You are editing a draft (v
              <Var>{currentVersion.version}</Var>). Save as draft or publish
              when ready.
            </T>
          </AlertDescription>
        </AlertContainer>
      )}

      <EmailTemplateForm
        key={viewingVersion?.id ?? template?.id}
        template={templateForForm}
        draftVersionId={isViewingDraft ? currentVersion?.id : null}
        onCancel={isCreateMode ? returnToList : handleCancelEdit}
        onSaved={isCreateMode ? returnToList : handleSaved}
        expandedJsonEditor
        readOnly={readOnly}
      />

      {!isCreateMode && template && (
        <EmailTemplateVersionHistoryPanel
          open={isVersionHistoryOpen}
          onClose={() => setIsVersionHistoryOpen(false)}
          emailTemplateId={template.id}
          pinnedVersionId={template.pinnedVersionId}
        />
      )}
    </div>
  );
}
